import * as React from "react";
import {
  Typography,
  Box,
  Container,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import NewCard from "../app/components/NewCard";

export const TERMS_SECTIONS = [
  {
    title: "1. Introduction & Acceptance of Terms",
    content: "These Terms & Conditions (\"Terms\") govern the provision of services by Chainz Solutions (\"Chainz,\" \"we,\" \"us,\" or \"our\"), a tech-enabled fulfillment center and logistics solutions provider. By accessing our website, entering into a service agreement, onboarding inventory, or using any of our services, the client (\"Client,\" \"you,\" or \"your\") acknowledges that it has read, understood, and agreed to be legally bound by these Terms. If the Client does not agree to these Terms, it must refrain from using Chainz Solutions' services.",
  },
  {
    title: "2. Definitions & Interpretation",
    content: "For the purposes of these Terms:\n• \"Services\" means all logistics, warehousing, fulfillment, inventory management, order processing, delivery coordination, returns handling, and technology services provided by Chainz.\n• \"Inventory\" means any goods, products, or materials stored, handled, or processed by Chainz on behalf of the Client.\n• \"Agreement\" means these Terms together with any commercial agreement, service order, pricing schedule, or written addendum agreed between the parties. Headings are for convenience only and do not affect interpretation.",
  },
  {
    title: "3. Scope of Services",
    content: "Chainz Solutions provides end-to-end fulfillment and logistics services, which may include, but are not limited to:\n• Warehousing and storage\n• Inventory receiving, handling, and management\n• Order fulfillment and dispatch\n• Last-mile delivery coordination through third parties\n• Returns processing\n• Logistics technology and reporting tools\nChainz may perform Services across multiple warehouses, branches, or operational locations. Services are provided strictly in accordance with the applicable Agreement.",
  },
  {
    title: "4. Client Obligations & Responsibilities",
    content: "The Client shall:\n• Provide accurate, complete, and timely information regarding inventory, orders, and documentation\n• Ensure all inventory complies with applicable laws and regulations\n• Properly package, label, and describe all goods\n• Maintain appropriate insurance coverage for inventory unless otherwise agreed\n• Pay all fees in accordance with agreed terms\nChainz shall not be liable for errors or delays resulting from inaccurate or incomplete Client information.",
  },
  {
    title: "5. Inventory Handling, Storage & Risk Allocation",
    content: "Chainz shall exercise commercially reasonable care in handling and storing inventory. Unless otherwise agreed in writing, risk of loss, damage, or deterioration of inventory remains with the Client at all times, including while inventory is stored at Chainz facilities. Chainz is not an insurer and does not provide insurance coverage for Client inventory.",
  },
  {
    title: "6. Order Fulfillment & Delivery Disclaimer",
    content: "Chainz shall use reasonable efforts to fulfill orders based on Client instructions. Delivery timelines are estimates only and may be affected by third-party carriers, force majeure events, or operational constraints. Chainz does not guarantee delivery times and shall not be liable for delays, missed delivery windows, or carrier-related failures.",
  },
  {
    title: "7. Service Levels & Performance Limitations",
    content: "Any service levels, performance metrics, or KPIs shall apply only if explicitly agreed in writing. Failure to meet non-contractual service expectations shall not constitute a breach of these Terms. Chainz does not guarantee uninterrupted or error-free operations.",
  },
  {
    title: "8. Pricing, Fees, Invoicing & Payment Terms",
    content: "All fees are charged according to the applicable pricing agreement. Invoices are payable within the agreed payment period. Late payments may result in service suspension and/or administrative charges. All fees are exclusive of taxes, duties, or governmental charges, which shall be borne by the Client.",
  },
  {
    title: "9. Subscriptions, Renewals & Modifications",
    content: "Subscription-based services shall renew automatically unless terminated in accordance with the Agreement. Chainz reserves the right to modify service offerings, pricing structures, or operational processes with reasonable notice, subject to contractual obligations.",
  },
  {
    title: "10. Intellectual Property & Systems Ownership",
    content: "All systems, software, processes, data structures, and intellectual property used or developed by Chainz remain the exclusive property of Chainz Solutions. The Client receives a limited, non-transferable right to use Chainz systems solely for accessing Services.",
  },
  {
    title: "11. Confidentiality & Data Protection",
    content: "Each party shall treat all confidential information received from the other party as strictly confidential. Chainz shall process Client data in accordance with applicable data protection laws and internal security policies. Confidentiality obligations survive termination of the Agreement.",
  },
  {
    title: "12. Technology Platform Usage",
    content: "Where Chainz provides access to digital platforms or dashboards, such access is granted on an \"as-is\" basis. Chainz may suspend or restrict access for security, maintenance, or compliance reasons without liability.",
  },
  {
    title: "13. Liability Limitations & Indemnification",
    content: "To the maximum extent permitted by law:\n• Chainz shall not be liable for indirect, incidental, consequential, or special damages\n• Chainz's total liability shall not exceed the fees paid by the Client in the three (3) months preceding the claim\nThe Client agrees to indemnify and hold Chainz harmless against claims arising from Client inventory, instructions, or legal non-compliance.",
  },
  {
    title: "14. Force Majeure",
    content: "Chainz shall not be liable for failure or delay caused by events beyond its reasonable control, including but not limited to natural disasters, labor disputes, government actions, or transportation disruptions.",
  },
  {
    title: "15. Termination & Suspension of Services",
    content: "Chainz may suspend or terminate Services for non-payment, legal risk, or material breach by the Client. Termination does not relieve the Client of outstanding payment obligations.",
  },
  {
    title: "16. Compliance with Laws & Regulations",
    content: "The Client warrants that all inventory and activities comply with applicable Egyptian laws and regulations. Chainz may refuse to handle goods that pose legal, safety, or regulatory risks.",
  },
  {
    title: "17. Dispute Resolution & Governing Law",
    content: "These Terms shall be governed by and construed in accordance with the laws of the Arab Republic of Egypt. Any disputes shall be subject to the exclusive jurisdiction of the competent courts of Egypt.",
  },
  {
    title: "18. Amendments & Updates to Terms",
    content: "Chainz reserves the right to update these Terms at any time. Updated Terms shall be effective upon publication on the website.",
  },
  {
    title: "19. Entire Agreement & Severability",
    content: "These Terms constitute the entire agreement between the parties regarding website and general service use. If any provision is held unenforceable, the remaining provisions shall remain in full force and effect.",
  },
  {
    title: "20. Contact Information",
    content: "Chainz Solutions. For legal or contractual inquiries, please contact us through the official communication channels listed on our website.",
  },
];

const RETURNS_SECTIONS = [
  {
    title: "1. Purpose of This Policy",
    content: "This Returns & Exchange Policy outlines the terms under which Chainz Solutions (\"Chainz,\" \"we,\" \"us\") manages product returns, exchanges, and reverse logistics on behalf of its clients (\"Client,\" \"you\"). This policy applies to all inventory processed, stored, or fulfilled by Chainz Solutions unless otherwise agreed in writing.",
  },
  {
    title: "2. Scope of Services",
    content: "Chainz Solutions provides returns handling and exchange logistics as an operational service only. Chainz does not determine, approve, or reject return or exchange eligibility unless explicitly authorized by the Client. All return and exchange rules are defined by the Client's own commercial policies toward its end customers.",
  },
  {
    title: "3. Client Responsibility for Return & Exchange Rules",
    content: "The Client is solely responsible for:\n• Defining return and exchange eligibility criteria\n• Determining time limits, refund methods, and exchange conditions\n• Ensuring return policies are legally compliant and clearly communicated to end customers\nChainz executes returns strictly based on Client-provided instructions and shall not be liable for customer dissatisfaction or policy disputes.",
  },
  {
    title: "4. Return Authorization Process",
    content: "All returns must be pre-authorized by the Client through one of the following methods:\n• Approved return requests submitted via Chainz systems\n• Written operational instructions\n• Integrated platform workflows\nUnauthorized or unidentified returns may be rejected, quarantined, or processed at the Client's expense.",
  },
  {
    title: "5. Condition Assessment of Returned Items",
    content: "Upon receipt, returned items may be classified as: Resalable, Non-resalable, Damaged, or Defective. Chainz performs basic visual and quantity checks only and does not conduct technical, functional, or quality testing unless contractually agreed. Final disposition decisions remain the responsibility of the Client.",
  },
  {
    title: "6. Exchanges",
    content: "Exchanges are processed only if:\n• The Client has explicitly enabled exchange workflows\n• Replacement inventory is available\n• Clear exchange instructions are provided\nChainz is not responsible for stock shortages, pricing differences, or exchange eligibility disputes.",
  },
  {
    title: "7. Handling of Damaged, Defective, or Unsellable Items",
    content: "Returned items deemed damaged, defective, expired, or unsellable shall be handled according to Client instructions, which may include: Return to Client, Disposal, Quarantine, or Write-off. If no instructions are provided within a reasonable timeframe, Chainz reserves the right to take commercially reasonable action at the Client's cost.",
  },
  {
    title: "8. Inventory Adjustments & Reporting",
    content: "All processed returns and exchanges will be reflected in inventory records based on actual received quantities and assessed condition. Chainz reports operational data but does not guarantee reconciliation with Client sales platforms due to external system dependencies.",
  },
  {
    title: "9. Fees & Charges",
    content: "Returns and exchanges may incur additional fees, including but not limited to: Receiving and inspection, Repackaging, Restocking, Disposal or reverse shipping. All applicable fees are charged according to the Client's pricing agreement with Chainz.",
  },
  {
    title: "10. Risk & Liability",
    content: "Risk of loss or damage to returned inventory remains with the Client at all times. Chainz is not liable for: Customer misuse or wear, Manufacturing defects, Transit damage prior to receipt, Incorrect return instructions. Chainz's liability, if any, is limited in accordance with the Terms & Conditions.",
  },
  {
    title: "11. Third-Party Carriers",
    content: "Chainz may coordinate return shipments using third-party carriers. Chainz is not responsible for delays, losses, or damages caused by third-party logistics providers.",
  },
  {
    title: "12. Compliance & Restricted Items",
    content: "The Client warrants that returned items comply with all applicable laws and regulations. Chainz reserves the right to refuse or isolate items that pose safety, legal, or regulatory risks.",
  },
  {
    title: "13. Policy Modifications",
    content: "Chainz Solutions reserves the right to update this Returns & Exchange Policy at any time. Updates become effective upon publication on the website unless otherwise contractually restricted.",
  },
  {
    title: "14. Governing Law",
    content: "This policy is governed by the laws of the Arab Republic of Egypt and shall be interpreted in accordance with applicable regulations.",
  },
  {
    title: "15. Contact Information",
    content: "For operational or policy-related inquiries regarding returns and exchanges, Clients should contact Chainz Solutions through official communication channels listed on the website.",
  },
];

const SectionContent = ({ content }) => (
  <Typography
    variant="body1"
    sx={{
      color: "var(--secColor4)",
      whiteSpace: "pre-line",
      lineHeight: 1.7,
    }}
  >
    {content}
  </Typography>
);

export const TermsAndConditions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        px: { xs: 2, sm: 3 },
        py: 3,
      }}
    >
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            color: "var(--secColor4)",
            fontWeight: 700,
            fontSize: isMobile ? "1.75rem" : "2rem",
            mb: 1,
            position: "relative",
            display: "inline-block",
          }}
        >
          Terms & Conditions
          <Box
            component="span"
            sx={{
              position: "absolute",
              bottom: -4,
              left: 0,
              right: 0,
              height: 6,
              borderRadius: 1,
              backgroundColor: "var(--primaryColor)",
              opacity: 0.45,
              zIndex: 0,
            }}
          />
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTabs-root": { minHeight: 48 },
          "& .MuiTab-root": {
            color: "var(--secColor4)",
            textTransform: "none",
            fontWeight: 500,
            fontSize: isMobile ? "0.9rem" : "1rem",
            "&.Mui-selected": {
              color: "var(--primaryColor)",
              fontWeight: 600,
            },
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "var(--primaryColor)",
            height: 3,
          },
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant={isMobile ? "fullWidth" : "standard"}
          aria-label="terms tabs"
        >
          <Tab label="Terms & Conditions" />
          <Tab label="Returns & Exchange Policy" />
        </Tabs>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {activeTab === 0 &&
          TERMS_SECTIONS.map((section, index) => (
            <NewCard
              key={index}
              content={
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ color: "var(--primaryColor)", mb: 1.5 }}
                  >
                    {section.title}
                  </Typography>
                  <SectionContent content={section.content} />
                </Box>
              }
            />
          ))}

        {activeTab === 1 &&
          RETURNS_SECTIONS.map((section, index) => (
            <NewCard
              key={index}
              content={
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ color: "var(--primaryColor)", mb: 1.5 }}
                  >
                    {section.title}
                  </Typography>
                  <SectionContent content={section.content} />
                </Box>
              }
            />
          ))}
      </Box>
    </Container>
  );
};

export default TermsAndConditions;
